import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Store as ShopIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import api from '../../config/api';
import { brandService } from '../../services/brandService';

const TikTokShopSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [shops, setShops] = useState([]);
  const [brandId, setBrandId] = useState(null);
  const [tempId, setTempId] = useState(null);

  const [brands, setBrands] = useState([]);
  const [selectedShops, setSelectedShops] = useState([]);
  const [shopBrandMapping, setShopBrandMapping] = useState({});

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }

    setTempId(temp_id);
    loadTikTokShops(temp_id);
    fetchBrands();
  }, [searchParams]);

  const fetchBrands = async () => {
    try {
      const brandsData = await brandService.getBrands();
      setBrands(brandsData || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const loadTikTokShops = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/tiktok-shop/shops', {
        params: { temp_id }
      });

      if (response.data.success) {
        setShops(response.data.shops || []);
        setBrandId(response.data.brand_id);
      } else {
        setError(response.data.message || 'Failed to load TikTok Shop accounts');
      }
    } catch (error) {
      console.error('Error loading TikTok Shop accounts:', error);
      setError(error.response?.data?.message || 'Failed to load TikTok Shop accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleShopSelection = (shopId, checked) => {
    if (checked) {
      const shop = shops.find(s => (s.shop_id || s.id) === shopId);
      setSelectedShops([...selectedShops, shop]);
      // For already-connected shops, default to their existing brand (re-auth flow).
      // Otherwise use the brand from the OAuth flow.
      const defaultBrand = shop?.alreadyConnected && shop?.connectedBrandId
        ? String(shop.connectedBrandId)
        : brandId;
      setShopBrandMapping(prev => ({
        ...prev,
        [shopId]: defaultBrand
      }));
    } else {
      setSelectedShops(selectedShops.filter(s => (s.shop_id || s.id) !== shopId));
      setShopBrandMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[shopId];
        return newMapping;
      });
    }
  };

  const handleBrandChange = (shopId, newBrandId) => {
    setShopBrandMapping(prev => ({
      ...prev,
      [shopId]: newBrandId
    }));
  };

  const handleCreateConnectors = async () => {
    if (selectedShops.length === 0) {
      setError('Please select at least one TikTok Shop');
      return;
    }

    const shopsWithoutBrands = selectedShops.filter(shop =>
      !shopBrandMapping[shop.shop_id || shop.id]
    );
    if (shopsWithoutBrands.length > 0) {
      setError('Please select a brand for all selected shops');
      return;
    }

    setCreating(true);
    setError('');

    try {
      const createdConnectors = [];

      for (const shop of selectedShops) {
        const shopId = shop.shop_id || shop.id;
        const selectedBrandId = shopBrandMapping[shopId] || brandId;

        const selectedBrand = brands.find(b => b.id === parseInt(selectedBrandId));
        const brandName = selectedBrand ? selectedBrand.name : 'Unknown Brand';

        const connectorName = `TikTok Shop - ${brandName} - ${shop.shop_name || shop.name || shopId}`;

        const response = await api.post('/api/connectors/tiktok-shop/create', {
          temp_id: tempId,
          shop_id: shopId,
          shop_cipher: shop.shop_cipher || shop.cipher || '',
          shop_name: shop.shop_name || shop.name || '',
          connector_name: connectorName,
          brand_id: parseInt(selectedBrandId)
        });

        if (response.data.success) {
          createdConnectors.push(response.data.connector);
        } else {
          throw new Error(`Failed to create connector for ${shop.shop_name || shop.name}: ${response.data.message}`);
        }
      }

      try {
        await api.post('/api/connectors/tiktok-shop/cleanup', { temp_id: tempId });
      } catch (cleanupError) {
        console.warn('Failed to cleanup temp data:', cleanupError);
      }

      const uniqueBrands = [...new Set(selectedShops.map(shop =>
        getBrandName(shopBrandMapping[shop.shop_id || shop.id] || brandId)
      ))];

      navigate('/connectors', {
        state: {
          message: `Successfully created ${createdConnectors.length} TikTok Shop connector(s) across ${uniqueBrands.length} brand(s): ${uniqueBrands.join(', ')}`,
          severity: 'success'
        }
      });

    } catch (err) {
      console.error('Error creating connectors:', err);
      setError(err.message || 'Failed to create connectors');
    } finally {
      setCreating(false);
    }
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => parseInt(b.id) === parseInt(brandId));
    return brand ? brand.name : 'Unknown Brand';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'CLOSED': case 'DEACTIVATED': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading TikTok Shop accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/connectors')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <ShopIcon sx={{ color: '#FE2C55', mr: 2, fontSize: 32 }} />
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            TikTok Shop Selection
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select which TikTok Shop(s) to connect for order, product, and settlement data
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select TikTok Shops
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose one or more TikTok Shops to connect. Each shop will sync orders, products, returns, and settlement data.
          </Typography>

          {shops.length === 0 ? (
            <Alert severity="warning">
              No authorized shops found. Make sure your TikTok Shop account has active shops.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {shops.map((shop) => {
                const shopId = shop.shop_id || shop.id;
                return (
                  <Grid item xs={12} md={6} key={shopId}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedShops.some(s => (s.shop_id || s.id) === shopId)}
                              onChange={(e) => handleShopSelection(shopId, e.target.checked)}
                              disabled={creating}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: '#FE2C55', mr: 2 }}>
                                  <ShopIcon />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {shop.shop_name || shop.name || `Shop ${shopId}`}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {shopId}
                                  </Typography>
                                  {shop.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={shop.connectedBrandName
                                          ? `Connected · ${shop.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={shop.status || 'ACTIVE'}
                                  color={getStatusColor(shop.status)}
                                />
                                {shop.region && (
                                  <Typography variant="caption" color="text.secondary">
                                    Region: {shop.region}
                                  </Typography>
                                )}
                              </Box>

                              {selectedShops.some(s => (s.shop_id || s.id) === shopId) && (
                                <Box sx={{ mt: 2 }}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel id={`brand-select-${shopId}`}>Assign to Brand</InputLabel>
                                    <Select
                                      labelId={`brand-select-${shopId}`}
                                      value={shopBrandMapping[shopId] || ''}
                                      label="Assign to Brand"
                                      onChange={(e) => handleBrandChange(shopId, e.target.value)}
                                      disabled={creating}
                                      sx={{ borderRadius: 1 }}
                                    >
                                      {brands.map(brand => (
                                        <MenuItem key={brand.id} value={brand.id}>
                                          {brand.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {shops.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedShops.length} shop(s) selected
          </Typography>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/connectors')}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreateConnectors}
              disabled={selectedShops.length === 0 || creating}
              startIcon={creating ? <CircularProgress size={20} /> : <CheckIcon />}
              sx={{
                backgroundColor: '#FE2C55',
                '&:hover': { backgroundColor: '#E0274D' }
              }}
            >
              {creating ? 'Creating...' : `Create ${selectedShops.length} Connector(s)`}
            </Button>
          </Box>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>What data will be synced?</strong>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          Orders (including line items, customer info, and payment details)<br />
          Products (catalog, SKUs, inventory, and pricing)<br />
          Returns and refunds<br />
          Settlements and financial data (commissions, fees, payouts)
        </Typography>
      </Alert>
    </Box>
  );
};

export default TikTokShopSelection;
